<template>
  <div class="foodleap-dd antialiased pb-24">

    <!-- Hero Section -->
    <section class="pt-24 pb-16 dd-bg-light relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-8 text-center relative z-10">
        <span class="dd-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase animate-on-scroll">
          STRATEGIC ANALYSIS
        </span>
        <h1 class="text-5xl md:text-7xl font-bold tracking-tight dd-text-slate mb-8 leading-[1.1] animate-on-scroll delay-1 mt-6">
          Deep Dive Reports
        </h1>
        <p class="text-xl dd-text-slate-70 font-light max-w-2xl mx-auto mb-10 animate-on-scroll delay-2">
          Comprehensive vertical studies. From Alternative Proteins to Circular Packaging, we go deep into the mechanics.
        </p>
        <div class="tw-flex flex-wrap justify-center gap-3 animate-on-scroll delay-3">
          <span
            v-for="tag in heroTags"
            :key="tag"
            class="dd-pill-tag px-5 py-2 rounded-full text-sm font-semibold">
            {{ tag }}
          </span>
        </div>
      </div>
      <div class="dd-blob absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-15"></div>
      <div class="dd-blob-2 absolute -bottom-20 -left-20 w-72 h-72 rounded-full blur-3xl opacity-12"></div>
    </section>

    <!-- Free Sample Form -->
    <section class="py-20 bg-white">
      <div class="max-w-6xl mx-auto px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          <!-- Left: Form -->
          <div class="animate-on-scroll">
            <h2 class="text-4xl font-bold dd-text-slate mb-4 leading-tight">
              Get your first Deep Dive — free
            </h2>
            <p class="dd-text-slate-60 text-lg font-light mb-8 leading-relaxed">
              Enter your email below and we'll send you our latest report. No account needed.
            </p>

            <!-- Success state -->
            <div v-if="submitted" class="dd-success-state text-center py-8">
              <div class="dd-success-icon w-16 h-16 rounded-full tw-flex items-center justify-center mx-auto mb-5">
                <Icon name="check-circle" size="30"/>
              </div>
              <h3 class="text-2xl font-bold dd-text-slate mb-3">Check your inbox!</h3>
              <p class="dd-text-slate-60 text-lg font-light">
                We've sent the report to <strong class="dd-text-slate font-bold">{{ email }}</strong>. It should arrive within a few minutes.
              </p>
            </div>

            <!-- Coming soon state -->
            <div v-else class="space-y-4">
              <button disabled class="w-full py-4 rounded-full font-bold text-base cursor-not-allowed opacity-50 border"
                      style="border-color: rgba(217,226,218,0.8); color: rgba(33,36,74,0.4);">Coming soon
              </button>
            </div>
          </div>

          <!-- Right: Report mockup -->
          <div class="animate-on-scroll delay-1">
            <div class="dd-report-mockup rounded-2xl overflow-hidden shadow-2xl relative" style="min-height: 380px;">
              <!-- Header bar -->
              <div class="dd-report-header tw-flex items-center justify-between px-6 py-4">
                <span class="text-xs font-black tracking-widest uppercase text-white/50">DEEP DIVE REPORT</span>
                <span class="dd-report-label text-xs font-bold px-2.5 py-1 rounded-full">Q1 2025</span>
              </div>
              <!-- Cover content -->
              <div class="px-8 pt-6 pb-8">
                <div class="dd-report-topic-tag text-xs font-bold tracking-widest uppercase mb-4">Alternative Proteins</div>
                <h3 class="text-2xl font-bold text-white leading-snug mb-6">
                  Alternative Proteins: Market Landscape 2025
                </h3>
                <!-- Blurred content lines -->
                <div class="space-y-2.5 mb-8">
                  <div class="h-2.5 rounded-full bg-white/10 w-full"></div>
                  <div class="h-2.5 rounded-full bg-white/10 w-5/6"></div>
                  <div class="h-2.5 rounded-full bg-white/8 w-full"></div>
                  <div class="h-2.5 rounded-full bg-white/10 w-4/5"></div>
                  <div class="h-2.5 rounded-full bg-white/8 w-full"></div>
                  <div class="h-2.5 rounded-full bg-white/10 w-3/4"></div>
                </div>
                <!-- Stats row -->
                <div class="tw-flex gap-6 mb-8">
                  <div>
                    <div class="text-2xl font-bold text-white">€4.2B</div>
                    <div class="text-xs text-white/40 mt-0.5">EU Investment</div>
                  </div>
                  <div class="w-px bg-white/10"></div>
                  <div>
                    <div class="text-2xl font-bold text-white">127</div>
                    <div class="text-xs text-white/40 mt-0.5">Organisations</div>
                  </div>
                  <div class="w-px bg-white/10"></div>
                  <div>
                    <div class="text-2xl font-bold text-white">38</div>
                    <div class="text-xs text-white/40 mt-0.5">Pages</div>
                  </div>
                </div>
                <!-- Watermark -->
                <div class="tw-flex items-center justify-between">
                  <div class="tw-flex items-center gap-2">
                    <div class="dd-watermark-dot w-2 h-2 rounded-full"></div>
                    <span class="text-xs font-black tracking-[0.2em] text-white/30 uppercase">FOODLEAP</span>
                  </div>
                  <span class="text-xs text-white/20">foodleap.com</span>
                </div>
              </div>
              <!-- Subtle overlay gradient at bottom -->
              <div class="absolute bottom-0 left-0 right-0 h-16 dd-report-fade pointer-events-none"></div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- Executive Summary -->
    <section class="py-20 bg-white">
      <div class="max-w-3xl mx-auto px-8">
        <div class="text-center mb-12 animate-on-scroll">
          <span class="dd-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6">
            SAMPLE REPORT
          </span>
          <h2 class="text-4xl font-bold dd-text-slate mb-3 mt-6">Executive Summary</h2>
          <p class="dd-text-slate-60 font-light">Alternative Proteins: Market Landscape 2025</p>
        </div>

        <div class="dd-exec-doc animate-on-scroll delay-1 relative">
          <!-- Document header -->
          <div class="dd-exec-header tw-flex items-center justify-between px-8 py-4 rounded-t-2xl">
            <div class="tw-flex items-center gap-3">
              <div class="dd-watermark-dot w-2 h-2 rounded-full"></div>
              <span class="text-xs font-black tracking-[0.2em] uppercase" style="color: rgba(33,36,74,0.35)">FOODLEAP DEEP DIVE</span>
            </div>
            <span class="dd-exec-tag text-xs font-bold px-2.5 py-1 rounded-full">Q1 2025</span>
          </div>

          <!-- Document body -->
          <div class="px-8 pt-8 pb-0">
            <div class="dd-exec-label text-xs font-black tracking-widest uppercase mb-6">01 — Executive Summary</div>

            <div class="space-y-5 dd-exec-body text-base leading-relaxed">
              <p>The protein transition is entering a new phase.</p>

              <p>The initial wave, driven by plant-based substitution, has delivered important innovation but is now reaching its limits. Growth in fully plant-based categories is
                slowing, as consumers remain unwilling to compromise on taste, price, and familiarity. At the same time, retail systems continue to prioritize predictability,
                margin, and rotation, limiting the scalability of products that do not fit existing category dynamics.</p>

              <p>As a result, the market is shifting away from replacement towards a more gradual and system-compatible approach.</p>

              <p>Balanced protein products, combining animal and alternative protein sources, are emerging as a key pathway. Rather than requiring consumers to change behavior,
                these products enable incremental improvement within familiar formats. Early market evidence shows that this approach aligns more effectively with both consumer
                expectations and retail requirements.</p>

              <p>Adoption is primarily driven by taste, price, and usability, often supported by "invisible" innovation, where product reformulation is not explicitly communicated.
                This allows change to occur without disrupting established consumption patterns.</p>

              <p>However, scaling this model remains challenging. The primary barrier is not a lack of innovation or demand, but the structure of the system itself. Retail
                procurement processes, category management, and data infrastructures are not designed to recognize or support gradual product transformation. As a result, balanced
                protein remains underrepresented in both shelf strategy and market data.</p>

              <p>The next phase of the protein transition will therefore be defined by alignment rather than disruption.</p>

              <p>Companies that succeed will focus on:</p>

              <ul class="dd-exec-list space-y-2 pl-0 ml-0">
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>reformulating high-volume products rather than creating entirely new categories</span>
                </li>
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>designing products that fit existing retail and consumer dynamics</span>
                </li>
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>leveraging hybrid models as a scalable pathway</span>
                </li>
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>aligning innovation with system constraints and incentives</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Fade + CTA overlay -->
          <div class="dd-exec-fade absolute bottom-0 left-0 right-0 h-48 rounded-b-2xl tw-flex flex-col items-center justify-end pb-8">
            <p class="dd-text-slate-60 text-sm mb-4 font-light">Continue reading — 38 pages of analysis</p>
            <button disabled class="px-8 py-3 rounded-full font-bold text-sm cursor-not-allowed opacity-50 border"
                    style="border-color: rgba(33,36,74,0.15); color: rgba(33,36,74,0.4);">Coming soon
            </button>
          </div>

          <!-- Bottom padding to make room for overlay -->
          <div class="h-48 rounded-b-2xl"></div>
        </div>
      </div>
    </section>

    <!-- Topics we cover -->
    <section class="py-20 dd-bg-light-grey">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-14 animate-on-scroll">
          <h2 class="text-4xl font-bold dd-text-slate mb-4">Topics we cover</h2>
          <p class="dd-text-slate-60 text-lg font-light">Each Deep Dive goes 40+ pages deep into a single vertical.</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(topic, i) in topics"
            :key="i"
            class="dd-topic-card animate-on-scroll"
            :class="i % 3 === 1 ? 'delay-1' : i % 3 === 2 ? 'delay-2' : ''">
            <div class="dd-topic-icon w-11 h-11 rounded-xl tw-flex items-center justify-center mb-4">
              <Icon :name="topic.icon" size="20"/>
            </div>
            <h3 class="text-lg font-bold dd-text-slate mb-2">{{ topic.title }}</h3>
            <p class="dd-text-slate-60 text-sm font-light leading-relaxed">{{ topic.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="py-24 dd-bg-slate">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-10 animate-on-scroll">
          <h2 class="text-3xl font-bold text-white mb-3">Get full access</h2>
          <p class="text-white/50 font-light">Everything you need to follow the food system transition.</p>
        </div>
        <div class="grid md:grid-cols-3 gap-6 items-center">
          <!-- Innovation Challenges -->
          <div class="dd-price-card rounded-2xl p-8 animate-on-scroll">
            <h3 class="text-base font-bold text-white mb-1">Innovation Challenges</h3>
            <p class="text-white/40 text-xs mb-6">Co-design open innovation with startups</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">€7,500</span>
              <span class="text-white/40 text-sm ml-1">/ challenge</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Ecosystem search</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Partner matchmaking</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Managed pilot design</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="dd-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">Contact us</button>
          </div>

          <!-- Ecosystem Insights (highlighted) -->
          <div class="dd-price-card dd-price-card-featured rounded-2xl p-10 relative animate-on-scroll delay-1">
            <div class="dd-popular-badge absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black tracking-widest uppercase">
              Most Popular
            </div>
            <h3 class="text-xl font-bold text-white text-center mb-1 mt-6">Ecosystem Insights</h3>
            <p class="text-white/40 text-sm text-center mb-8">All transition reports + live ecosystem data</p>
            <div class="text-center mb-8">
              <span class="text-5xl font-black text-white">€9,000</span>
              <span class="text-white/40 text-base ml-2">/ year</span>
            </div>
            <ul class="space-y-3 mb-10">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Transition Pulse — monthly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Innovation Radar — quarterly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Deep Dive Reports — annual</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Full ecosystem data access</span>
              </li>
            </ul>
            <button disabled class="dd-btn-blue w-full py-4 rounded-full font-bold text-base cursor-not-allowed opacity-50">Coming soon</button>
          </div>

          <!-- Transition Advisory -->
          <div class="dd-price-card rounded-2xl p-8 animate-on-scroll delay-2">
            <h3 class="text-base font-bold text-white mb-1">Transition Advisory</h3>
            <p class="text-white/40 text-xs mb-6">Bespoke strategy for your journey</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">Project</span>
              <span class="text-white/40 text-sm ml-1">based</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Bespoke deep dives</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Strategy workshop</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Executive coaching</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="dd-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">Talk to us</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Report Switcher Bar -->
    <div class="dd-rs-bar fixed bottom-6 left-1/2 -translate-x-1/2 z-50 tw-flex items-center gap-1 px-2 py-2 rounded-full shadow-2xl">
      <button @click="$router.push('/transition-pulse-report')" class="dd-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="wave-square" size="13"/>
        Pulse
      </button>
      <button @click="$router.push('/innovation-radar-report')" class="dd-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="bullseye" size="13"/>
        Radar
      </button>
      <button @click="$router.push('/deep-dive-report')" class="dd-rs-tab dd-rs-active tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="file-text" size="13"/>
        Deep Dive
      </button>
    </div>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'

  export default defineComponent({
    name: 'StaticHomeFoodleapDeepDivePage',
    components: { SimplifiedFooterFoodleapNew, Icon },

    data () {
      return {
        email: '',
        submitted: false,
        submitting: false,
        emailError: '',
        heroTags: [
          'Alternative Proteins',
          'Circular Packaging',
          'Fermentation',
          'Precision Ag',
        ],
        topics: [
          {
            icon: 'vial',
            title: 'Balanced Protein Goes Mainstream',
            desc: 'Products blending animal, plant and alternative protein are a practical way to improve nutrition and sustainability without changing eating habits.',
          },
          {
            icon: 'sync',
            title: 'Reformulation Over New Launches',
            desc: 'The biggest impact comes from adapting high-volume products, not from creating entirely new categories.',
          },
          {
            icon: 'eco',
            title: 'Taste & Texture Decide Everything',
            desc: 'No matter the story, products only scale if they meet expectations on flavour, mouthfeel, and usability.',
          },
          {
            icon: 'building',
            title: 'Shelf Space Is the Real Battlefield',
            desc: 'Retail decisions on listing, rotation, and pricing determine what succeeds.',
          },
          {
            icon: 'heart-outline',
            title: '"Healthy" Is Being Redefined',
            desc: 'From ultra-processed debates to functional ingredients, health positioning is becoming as important as sustainability.',
          },
          {
            icon: 'circle-nodes',
            title: 'Integration Beats Disruption',
            desc: 'Solutions that fit existing processes, supply chains, and consumer habits scale faster than those that try to replace them.',
          },
        ],
      }
    },

    methods: {
      handleSubscribe () {
        this.emailError = ''
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!this.email || !emailRegex.test(this.email)) {
          this.emailError = 'Please enter a valid email address.'
          return
        }
        this.submitting = true
        setTimeout(() => {
          this.submitting = false
          this.submitted = true
        }, 800)
      },
    },

    mounted () {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })
    },

    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
    },
  })
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-dd {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    // ── Brand color variables ──────────────────────────────────────────────────
    $ps: #21244A; // primary slate
    $pg: #5CCC61; // primary green
    $blg: #D9E2DA; // brand light grey
    $blue: #70C1FF; // secondary blue (accent for this page)
    $yel: #FFC83D; // secondary yellow
    $vg: #B5F167; // secondary vibrant green

    // ── Scroll-reveal ──────────────────────────────────────────────────────────
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s ease, transform 0.6s ease;

      &.delay-1 {
        transition-delay: 0.12s;
      }

      &.delay-2 {
        transition-delay: 0.24s;
      }

      &.delay-3 {
        transition-delay: 0.36s;
      }

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
    }

    // ── Color utilities ────────────────────────────────────────────────────────
    .dd-text-slate {
      color: $ps;
    }

    .dd-text-slate-60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .dd-text-slate-70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .dd-bg-slate {
      background-color: $ps;
    }

    .dd-bg-light {
      background: radial-gradient(circle at top center, rgba(112, 193, 255, 0.08) 0%, #ffffff 70%);
    }

    .dd-bg-light-grey {
      background-color: rgba(217, 226, 218, 0.25);
    }

    // ── Hero ───────────────────────────────────────────────────────────────────
    .dd-badge {
      background-color: rgba(112, 193, 255, 0.15);
      color: darken($blue, 25%);
      border: 1px solid rgba(112, 193, 255, 0.3);
    }

    .dd-blob {
      background-color: $blue;
    }

    .dd-blob-2 {
      background-color: $vg;
    }

    .dd-pill-tag {
      background-color: rgba(112, 193, 255, 0.1);
      color: darken($blue, 20%);
      border: 1px solid rgba(112, 193, 255, 0.25);
      transition: transform 0.2s, background-color 0.2s;
      cursor: default;

      &:hover {
        transform: translateY(-2px);
        background-color: rgba(112, 193, 255, 0.2);
      }
    }

    // ── Form ───────────────────────────────────────────────────────────────────
    .dd-email-input {
      color: $ps;
      border-color: $blg;
      background-color: #ffffff;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;

      &::placeholder {
        color: rgba(33, 36, 74, 0.35);
      }

      &:focus {
        border-color: $blue;
        box-shadow: 0 0 0 3px rgba(112, 193, 255, 0.2);
      }

      &.dd-input-error {
        border-color: #EF374E;
        box-shadow: 0 0 0 3px rgba(239, 55, 78, 0.1);
      }
    }

    .dd-error-text {
      color: #EF374E;
    }

    .dd-btn-submit {
      background-color: $ps;
      color: #ffffff;
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.2);
      }

      &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }
    }

    // ── Success state ──────────────────────────────────────────────────────────
    .dd-success-state {
      border: 1px solid rgba(92, 204, 97, 0.25);
      border-radius: 1.25rem;
      background-color: rgba(92, 204, 97, 0.04);
      padding: 2.5rem 2rem;
    }

    .dd-success-icon {
      background-color: rgba(92, 204, 97, 0.15);
      color: darken(#5CCC61, 8%);
    }

    // ── Report mockup ──────────────────────────────────────────────────────────
    .dd-report-mockup {
      background-color: $ps;
      border: 1px solid rgba(255, 255, 255, 0.05);
    }

    .dd-report-header {
      background-color: rgba(255, 255, 255, 0.04);
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }

    .dd-report-label {
      background-color: rgba(112, 193, 255, 0.2);
      color: $blue;
    }

    .dd-report-topic-tag {
      color: $blue;
      letter-spacing: 0.15em;
    }

    .dd-watermark-dot {
      background-color: $pg;
    }

    .dd-report-fade {
      background: linear-gradient(to top, $ps 0%, transparent 100%);
    }

    // ── Executive Summary ──────────────────────────────────────────────────────
    .dd-exec-doc {
      border: 1px solid $blg;
      border-radius: 1.25rem;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(33, 36, 74, 0.07);
    }

    .dd-exec-header {
      background-color: rgba(217, 226, 218, 0.25);
      border-bottom: 1px solid $blg;
    }

    .dd-exec-tag {
      background-color: rgba(112, 193, 255, 0.15);
      color: darken($blue, 25%);
    }

    .dd-exec-label {
      color: $blue;
      letter-spacing: 0.15em;
    }

    .dd-exec-body {
      color: rgba(33, 36, 74, 0.8);
      font-weight: 300;

      p:first-child {
        font-size: 1.1rem;
        font-weight: 500;
        color: $ps;
      }
    }

    .dd-exec-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .dd-exec-bullet {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background-color: $blue;
    }

    .dd-exec-fade {
      background: linear-gradient(to top, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0.6) 70%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;

      > * {
        pointer-events: auto;
      }
    }

    // ── Topic cards ────────────────────────────────────────────────────────────
    .dd-topic-card {
      background-color: #ffffff;
      border: 1px solid $blg;
      border-radius: 1.25rem;
      padding: 1.75rem;
      transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.08);
        border-color: rgba(112, 193, 255, 0.35);
      }
    }

    .dd-topic-icon {
      background-color: rgba(112, 193, 255, 0.12);
      color: darken($blue, 20%);
    }

    // ── Pricing block ──────────────────────────────────────────────────────────
    .dd-price-card-featured {
      border-color: rgba(112, 193, 255, 0.4);
      box-shadow: 0 0 0 1px rgba(112, 193, 255, 0.2);
    }

    .dd-btn-ghost {
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.6);
      transition: border-color 0.2s, color 0.2s;

      &:hover {
        border-color: rgba(255, 255, 255, 0.5);
        color: rgba(255, 255, 255, 0.9);
      }
    }

    .dd-price-card {
      background-color: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .dd-popular-badge {
      background-color: $blue;
      color: $ps;
    }

    .dd-check-icon {
      color: $blue;
      flex-shrink: 0;
    }

    // ── Report switcher bar ────────────────────────────────────────────────────
    .dd-rs-bar {
      background-color: $ps;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .dd-rs-tab {
      color: rgba(255, 255, 255, 0.45);
      transition: all 0.2s;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    .dd-rs-active {
      background-color: $blue;
      color: $ps !important;
    }

    // ── CTA button ─────────────────────────────────────────────────────────────
    .dd-btn-blue {
      background-color: $blue;
      color: $ps;
      font-weight: 700;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(112, 193, 255, 0.4);
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }
  }
</style>
